import clsx from "clsx";
import React from "react";
import {
  TableRow,
  TableRowProps,
  TableCell,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { ProgramTemplateDto } from "@growth-machine-llc/stridist-api-client";
import { useTheme } from "@mui/material";
import { ProgramTemplateMenu } from "./ProgramTemplateMenu";
import { ProgramTemplateTypeBadge } from "./ProgramTemplatePrivacyBadge";
import { ProgramCategoryBadge } from "./ProgramCategoryBadge";
import SeeMoreNotes from "../training-summary/SeeMoreNotes";
import { useUpdateTemplateCategoryMutation } from "../../hooks/useUpdateTemplateCategoryMutation";
import { ArrowForwardRounded } from "@mui/icons-material";
import { getProgramTemplateLibraryWidths } from "./ProgramsTableScreen";

const useStyles = makeStyles((theme) => ({
  root: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  thumbnail: {
    width: 70,
    height: 70,
    objectFit: "cover",
    borderRadius: theme.spacing(1),
    border: `2px solid ${theme.palette.primary.main}`,
  },
  thumbnailPlaceholder: {
    height: 80,
    borderRadius: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  name: {
    fontSize: 16,
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  category: {
    fontSize: 12,
    fontWeight: 500,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
  },
  subText: {
    color: theme.palette.text.secondary,
    fontWeight: "normal",
    hyphens: "auto",
    hyphenateCharacter: "auto",
    wordBreak: "break-word",
  },
  actions: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    width: "100%",
  },
}));

export interface ProgramsTableRowProps extends TableRowProps {
  template: ProgramTemplateDto;
  onSelectedTemplate?: (template: ProgramTemplateDto) => void;
  menuClassName?: string;
}

export function ProgramsTableRow(props: ProgramsTableRowProps) {
  const { className, template, onSelectedTemplate, menuClassName, ...other } =
    props;
  const s = useStyles();
  const theme = useTheme();

  const canEdit = template.canEdit;

  const columnWidths = getProgramTemplateLibraryWidths();

  const { updateTemplateCategory } = useUpdateTemplateCategoryMutation();

  const handleClick = React.useCallback(() => {
    if (onSelectedTemplate) {
      onSelectedTemplate(template);
    }
  }, [onSelectedTemplate, template]);

  return (
    <TableRow
      className={clsx(s.root, className)}
      onClick={handleClick}
      hover
      {...other}
    >
      <TableCell sx={{ width: columnWidths.thumbnail }}>
        {template.image ? (
          <Box
            component="img"
            src={template.image}
            alt={template.name}
            className={s.thumbnail}
          />
        ) : (
          <Box
            className={s.thumbnailPlaceholder}
            sx={{
              backgroundColor: theme.palette.grey[200],
            }}
          >
            <Typography variant="caption" color="text.secondary">
              No image
            </Typography>
          </Box>
        )}
      </TableCell>
      <TableCell width={columnWidths.title}>
        <Box sx={{ width: "100%" }}>
          <Typography className={s.name}>{template.name}</Typography>
          {template.description && (
            <Typography variant="caption" className={s.subText}>
              <SeeMoreNotes rows={2}>{template.description}</SeeMoreNotes>
            </Typography>
          )}
        </Box>
      </TableCell>
      <TableCell width={columnWidths.category} align="center">
        <ProgramCategoryBadge
          template={template}
          onCategoryChange={(category) =>
            updateTemplateCategory({ id: template.id, category })
          }
          readonly={!canEdit}
        />
      </TableCell>
      <TableCell width={columnWidths.privacy} align="center">
        <ProgramTemplateTypeBadge
          template={template}
          size={42}
          showLabel={true}
        />
      </TableCell>
      <TableCell width={columnWidths.actions} align="center">
        <Box className={s.actions} onClick={(e) => e.stopPropagation()}>
          <ProgramTemplateMenu
            template={template}
            buttonClassName={menuClassName}
          />
          {template.canEdit && (
            <IconButton
              children={<ArrowForwardRounded />}
              onClick={handleClick}
              size="large"
            />
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
}
